import type { ModuleAccent, TileSize } from '@nexus/core';
import type { IconName } from '@nexus/ui';
import { lazy, type ComponentType } from 'react';
import type { SessionInfo } from '../api/types';
import type { WebDashboardTile, WebModule } from '../modules/define';
import { accentOf } from '../modules/navigation';
import { canIn } from '../session/session';
import { KERNEL_TILES } from './kernel-tiles';

/** A tile the person may see, from the kernel or a module. */
export interface DashboardTile {
  key: string;
  /** i18n keys. */
  title: string;
  description?: string;
  size: TileSize;
  path?: string;
  icon: IconName;
  accent?: ModuleAccent;
  /** i18n key of the module name; kernel tiles have none. */
  source?: string;
  /** Content to the edges of the tile, e.g. a list. */
  flush?: boolean;
  Content: ComponentType;
}

// One lazy component per tile, so React keeps it loaded between renders.
const lazyTiles = new WeakMap<WebDashboardTile, ComponentType>();

function contentOf(tile: WebDashboardTile): ComponentType {
  let component = lazyTiles.get(tile);
  if (!component) {
    component = lazy(tile.load);
    lazyTiles.set(tile, component);
  }
  return component;
}

/**
 * Tiles of the start page the person may see: kernel tiles first, then the tiles of active
 * modules (and active parents) the person has the permission for, in module order. The API behind
 * a tile checks again; this only hides it.
 */
export function availableTiles(
  modules: readonly WebModule[],
  session: SessionInfo,
): DashboardTile[] {
  const active = new Set(session.modules);
  const kernel = KERNEL_TILES.filter((tile) => tile.visible?.(modules, session) ?? true).map(
    ({ visible: _, ...tile }) => tile,
  );
  const fromModules = modules
    .filter(
      (m) => active.has(m.manifest.id) && (!m.manifest.parent || active.has(m.manifest.parent)),
    )
    .flatMap((module) =>
      (module.manifest.dashboard ?? [])
        .filter((tile) => canIn(session, tile.permission))
        .flatMap((tile): DashboardTile[] => {
          const component = module.dashboard?.find((c) => c.key === tile.key);
          if (!component) return [];
          return [
            {
              key: tile.key,
              title: tile.title,
              description: tile.description,
              size: tile.size ?? 'small',
              path: tile.path,
              icon: module.manifest.icon,
              accent: accentOf(module, modules),
              source: module.manifest.name,
              Content: contentOf(component),
            },
          ];
        }),
    );
  return [...kernel, ...fromModules];
}

/**
 * Every tile in a manifest has exactly one component in the web module, and every component a
 * declared tile. Runs when the router is built and in the tests of each module.
 */
export function checkDashboard(modules: readonly WebModule[]): void {
  const problems: string[] = [];
  for (const module of modules) {
    const { id } = module.manifest;
    const declared = new Set((module.manifest.dashboard ?? []).map((t) => t.key));
    const components = (module.dashboard ?? []).map((t) => t.key);
    for (const key of declared) {
      const count = components.filter((c) => c === key).length;
      if (count === 0) problems.push(`${id}: dashboard tile "${key}" has no component`);
      if (count > 1) problems.push(`${id}: dashboard tile "${key}" has ${count} components`);
    }
    for (const key of components) {
      if (!declared.has(key)) {
        problems.push(`${id}: dashboard component "${key}" is not declared in the manifest`);
      }
    }
  }
  if (problems.length > 0) throw new Error(problems.join('\n'));
}
